import { useEffect, useId, useRef, useState } from 'react'
import { IconChevron } from './Iconen'
import { staatInLijst, zoekGemeenten } from '../lib/gemeenten'

/*
 * Het gemeenteveld: kiezen uit de lijst of zelf typen (FR-06).
 *
 * Dezelfde combobox als op de bestaande site, met dezelfde toetsenbedieningen.
 * Wat eronder zit is wel veranderd: de lijst kwam daar uit de tarieventabel en
 * bepaalde de prijs. Sinds WP-08 bepaalt de gemeente niets meer, dus dit is puur
 * een invulhulp — en dat is precies waarom een naam die er niet bij staat gewoon
 * geaccepteerd wordt in plaats van afgekeurd.
 *
 * Het veld is daarom een tekstveld met suggesties en geen `<select>`: dertien van
 * de ruim driehonderd Nederlandse gemeenten mogen niemand tegenhouden.
 */

interface Eigenschappen {
  /** Melding van de validatie, of leeg als het veld in orde is. */
  fout?: string
  /** Wordt aangeroepen zodra de bezoeker een naam uit de lijst kiest. */
  bijKeuze: (naam: string) => void
}

export default function GemeenteCombobox({ fout, bijKeuze }: Eigenschappen) {
  const lijstId = useId()

  const [waarde, setWaarde] = useState('')
  const [open, setOpen] = useState(false)
  const [actief, setActief] = useState(-1)

  const veld = useRef<HTMLInputElement>(null)
  const lijst = useRef<HTMLUListElement>(null)
  const houder = useRef<HTMLDivElement>(null)

  // Bij een lege waarde de hele lijst, anders alleen wat erop lijkt.
  const zichtbaar = zoekGemeenten(waarde)
  const toon = open && zichtbaar.length > 0
  const optieId = (index: number) => `${lijstId}-optie-${index}`

  // Buiten het veld klikken sluit de lijst. Op mousedown en niet op click, want
  // anders sluit hij pas nadat de klik elders al effect heeft gehad.
  useEffect(() => {
    if (!open) return

    function bijMousedown(gebeurtenis: MouseEvent) {
      if (!houder.current?.contains(gebeurtenis.target as Node)) {
        setOpen(false)
        setActief(-1)
      }
    }

    document.addEventListener('mousedown', bijMousedown)
    return () => document.removeEventListener('mousedown', bijMousedown)
  }, [open])

  // De actieve optie in beeld houden bij navigeren met de pijltjes.
  useEffect(() => {
    if (actief < 0) return
    lijst.current?.children.item(actief)?.scrollIntoView({ block: 'nearest' })
  }, [actief])

  function kies(naam: string) {
    setWaarde(naam)
    setOpen(false)
    setActief(-1)
    veld.current?.focus()
    bijKeuze(naam)
  }

  function bijToets(gebeurtenis: React.KeyboardEvent<HTMLInputElement>) {
    if (gebeurtenis.key === 'ArrowDown' || gebeurtenis.key === 'ArrowUp') {
      gebeurtenis.preventDefault()

      if (!open) {
        setOpen(true)
        return
      }
      if (zichtbaar.length === 0) return

      setActief((huidig) =>
        gebeurtenis.key === 'ArrowDown'
          ? (huidig + 1) % zichtbaar.length
          : huidig <= 0
            ? zichtbaar.length - 1
            : huidig - 1,
      )
      return
    }

    if (gebeurtenis.key === 'Enter') {
      // Alleen onderscheppen als er iets te kiezen valt. Anders hoort Enter het
      // formulier te verzenden, net als vanuit elk ander veld.
      if (toon && actief > -1) {
        gebeurtenis.preventDefault()
        kies(zichtbaar[actief] ?? '')
      } else if (open) {
        setOpen(false)
      }
      return
    }

    if (gebeurtenis.key === 'Escape') {
      if (open) {
        // Niet doorgeven: Escape sluit hier de lijst en niet iets erboven.
        gebeurtenis.stopPropagation()
        setOpen(false)
        setActief(-1)
      }
      return
    }

    if (gebeurtenis.key === 'Tab') {
      setOpen(false)
      setActief(-1)
    }
  }

  const buitenDeLijst = waarde.trim() !== '' && !staatInLijst(waarde)

  return (
    <div className={fout ? 'fld c2 is-error' : 'fld c2'} ref={houder}>
      <label htmlFor="f-gemeente">
        Gemeente <span aria-hidden="true">*</span>
      </label>
      <div className="combo" id="gemeenteCombo">
        <input
          id="f-gemeente"
          name="gemeente"
          type="text"
          role="combobox"
          autoComplete="off"
          placeholder="Kies of typ uw gemeente"
          required
          ref={veld}
          value={waarde}
          aria-expanded={toon}
          aria-controls={lijstId}
          aria-autocomplete="list"
          aria-activedescendant={
            toon && actief > -1 ? optieId(actief) : undefined
          }
          aria-invalid={fout ? true : undefined}
          aria-describedby="e-gemeente h-gemeente"
          onChange={(gebeurtenis) => {
            setWaarde(gebeurtenis.target.value)
            setOpen(true)
            setActief(-1)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={bijToets}
        />
        <button
          type="button"
          className="combo__btn"
          id="gemeenteToggle"
          tabIndex={-1}
          aria-label="Lijst met gemeenten openen"
          onMouseDown={(gebeurtenis) => {
            // preventDefault, anders haalt de knop de focus bij het invoerveld
            // weg en sluit de lijst zichzelf meteen weer.
            gebeurtenis.preventDefault()
            setOpen((huidig) => !huidig)
            setActief(-1)
            veld.current?.focus()
          }}
        >
          <IconChevron />
        </button>
        {/*
          Altijd in de DOM, ook dicht: `aria-controls` op het invoerveld moet naar
          een bestaand element wijzen. Verbergen gebeurt met `hidden`, net als op
          de bestaande site.
        */}
        <ul
          className="combo__list"
          id={lijstId}
          role="listbox"
          aria-label="Gemeenten"
          hidden={!toon}
          ref={lijst}
        >
          {zichtbaar.map((naam, index) => (
            <li
              key={naam}
              id={optieId(index)}
              className={
                index === actief ? 'combo__opt is-active' : 'combo__opt'
              }
              role="option"
              aria-selected={
                naam.toLocaleLowerCase('nl') ===
                waarde.trim().toLocaleLowerCase('nl')
              }
              onMouseDown={(gebeurtenis) => {
                gebeurtenis.preventDefault()
                kies(naam)
              }}
            >
              {naam}
            </li>
          ))}
        </ul>
      </div>
      {/*
        De eerste zin van de bestaande site — "Uw gemeente bepaalt het tarief" —
        is vervallen: het bedrag is voor iedere gemeente gelijk. Wat de bestaande
        site zei als de gemeente er niet bij stond ("wij nemen contact met u op
        over het tarief") klopt daarmee ook niet meer, en is vervangen door wat er
        nu werkelijk gebeurt: niets bijzonders.
      */}
      <span className="hint" id="h-gemeente">
        {buitenDeLijst
          ? 'Deze gemeente staat niet in onze lijst. Dat is geen bezwaar — wij nemen uw aanvraag gewoon in behandeling.'
          : 'Staat uw gemeente er niet bij? Typ de naam dan zelf.'}
      </span>
      <span className="err" id="e-gemeente" role="alert">
        {fout}
      </span>
    </div>
  )
}
